// De facto control per hex at a date (ARCHITECTURE §3.5).
//
//   ControlEpisode (world.json) ──resolve geometry once──▶ layer {hexes, frac, code}
//        admin1 codes    → frac 255, code = episode status
//        partial codes   → the episode's own status; frac = the latest reported `extent` share ≤ D,
//                          else PARTIAL_FRAC (the unit is only partly held)
//        GeoJSON / points → raster.ts
//        series          → control/<id>.json, snapshot = latest one ≤ date
//
//   at date D: active layers, oldest `from` first; per hex the higher fraction wins and a tie
//   goes to the newer episode, except that an annexation claim still in force is never downgraded
//   by the same controller's occupation layer (Crimea stays 'annexation claimed' under the 2022
//   Ukraine series, which marks everything 'occupied'). A layer only marks hexes whose
//   default-worldview owner at D is the episode's `territory_of` (stops polygons and series
//   spilling into neighbours). Where the controller equals the owner under the leader's point of
//   view, the hex shows no control (Crimea under Russia's view is plain Russian territory; the
//   POV claim is the owner layer).
//
// A series may declare gaps (ControlSeries.gaps, [from, to)): dates with no observation. Inside one
// the snapshot carried forward is not the picture: no front line, no citation, and the hexes of the
// whole territory (every hex the default worldview gives `territory_of`, not only the land the
// snapshots on either side held) is painted CONTESTED with no controller and basis UNKNOWN, a share
// of GAP_FRAC that any recorded control outranks, so Crimea's annexation claim stays. Kyiv and
// Bucha were in neither snapshot's hexes, yet nobody observed them in March 2022 either.
//
// Returned arrays are SHARED and READ-ONLY, cached per (active layers, series snapshots, owner
// arrays). Snapshots reuse them across dates until the control picture changes.

import { HEX_COUNT } from '../core/grid.ts';
import type { ControlEpisode, ControlSeries, Country, DateStr, Source, SourceId } from '../core/schema.ts';
import { inSpan } from '../core/dates.ts';
import type { GeoJson } from '../data/types.ts';
import { CONTROL, type WorldSnapshot } from './api.ts';
import { indexOf, type ActorTable } from './actors.ts';
import { Lru } from './cache.ts';
import { FULL, admin1Hexes, pointsArea, polygonArea, type Admin1Index, type HexArea } from './raster.ts';

/** Stand-in fraction for a partly held admin-1 unit: the share is unknown, so it draws as half. */
export const PARTIAL_FRAC = 128;

/** Share painted on hexes whose control is unknown: the lowest, so any recorded control outranks it. */
const GAP_FRAC = 1;

/** How a hex's controller and share are known (ControlArrays.controlBasis). */
export const BASIS = {
  /** From the curated record: the geometry says who holds the hex. */
  RECORDED: 0,
  /** A unit only partly held: the share is a stand-in (PARTIAL_FRAC), not a measurement. */
  CONVENTION: 1,
  /** Inside a declared gap of a control series: nobody observed who held the hex. */
  UNKNOWN: 2,
  /** A unit only partly held, its share a reported figure for the whole unit (`extent`), not for this hex. */
  EXTENT: 3,
} as const;

/** Publisher whose `published` is the revision read, not the day of the events: its sources are never dropped for their date. */
const REVISED_PUBLISHER = 'Wikipedia';

/** Distinct control pictures kept; each is three per-hex arrays (~690 KB). */
const MAX_PICTURES = 12;

const STATUS_CODE: Record<ControlEpisode['status'], number> = {
  'occupied': CONTROL.OCCUPIED,
  'contested': CONTROL.CONTESTED,
  'annexation-claimed': CONTROL.ANNEXATION_CLAIMED,
  'breakaway': CONTROL.BREAKAWAY,
};

/** A dated report of the share of a unit an episode holds (world-state `extent`; not in the shared schema yet). */
export interface Extent {
  date: DateStr;
  of: string;
  share: number;
  sources: SourceId[];
  qualifier?: string;
}

/** ControlEpisode as curated: `extent` is not in the shared schema yet. */
type ExtentEpisode = ControlEpisode & { extent?: Extent[] };

export interface Layer {
  episode: ControlEpisode;
  controller: number;
  territory: number;
  hexes: Int32Array;
  frac: Uint8Array;
  /** CONTROL value per entry. */
  code: Uint8Array;
  /** BASIS value per entry. */
  basis: Uint8Array;
  /** Reported shares of the partly held units, oldest first. */
  extents: Extent[];
  /** frac and basis with the share of extents[k] on the partly held entries, made on first use. */
  reported: Array<{ frac: Uint8Array; basis: Uint8Array } | undefined>;
}

/** A hole in a control series: nothing was observed from `from` (inclusive) to `to` (exclusive). */
export interface SeriesGap {
  series: string;
  from: DateStr;
  to: DateStr;
  sources: SourceId[];
  reason: string;
}

/** ControlSeries as baked: `gaps` is not in the shared schema yet. */
type GappedSeries = ControlSeries & { gaps?: Array<Omit<SeriesGap, 'series'>> };

export interface SeriesLayer {
  episode: ControlEpisode;
  controller: number;
  territory: number;
  status: number;
  series: ControlSeries;
  /** Decoded snapshots, same order as series.snapshots. */
  snaps: HexArea[];
}

export interface ControlArrays {
  controller: Uint16Array;
  control: Uint8Array;
  controlFrac: Uint8Array;
  /** BASIS per hex: whether the share and controller are recorded, a convention or unknown. */
  controlBasis: Uint8Array;
}

/** The registry entry of a source id, for its publication date. */
export type SourceLookup = (id: SourceId) => Pick<Source, 'published' | 'publisher'> | undefined;

export interface ControlModel {
  layers: Layer[];
  series: SeriesLayer[];
  /** Registry lookup for `citedOn`; without one no source is dropped for its date. */
  source?: SourceLookup;
  cache: Lru<string, ControlArrays>;
  /** Identity of owner arrays, for the cache key. */
  arrayIds: WeakMap<Uint16Array, number>;
  lastId: number;
}

export interface ControlInputs {
  actors: ActorTable;
  admin1: Admin1Index;
  areaKm2: Float32Array;
  geometry: Map<string, GeoJson>;
  series: Map<string, ControlSeries>;
  warnings: string[];
  source?: SourceLookup;
}

// ─── build ───────────────────────────────────────────────────────────────────────────────

/** What a geometry makes of a layer; buildControl adds the episode's own fields. */
type LayerGeo = Omit<Layer, 'episode' | 'controller' | 'territory' | 'extents' | 'reported'>;

function admin1Layer(codes: string[], partial: string[], status: number, ix: Admin1Index): LayerGeo {
  const hexes: number[] = [];
  const frac: number[] = [];
  const code: number[] = [];
  const basis: number[] = [];
  const push = (list: string[], f: number, c: number, b: number) => {
    for (const unit of list) {
      for (const h of admin1Hexes(ix, unit)) {
        hexes.push(h);
        frac.push(f);
        code.push(c);
        basis.push(b);
      }
    }
  };
  push(partial, PARTIAL_FRAC, status, BASIS.CONVENTION);
  push(codes, FULL, status, BASIS.RECORDED);
  return { hexes: Int32Array.from(hexes), frac: Uint8Array.from(frac), code: Uint8Array.from(code), basis: Uint8Array.from(basis) };
}

function areaLayer(area: HexArea, status: number): LayerGeo {
  return { hexes: area.hexes, frac: area.frac, code: new Uint8Array(area.hexes.length).fill(status), basis: new Uint8Array(area.hexes.length).fill(BASIS.RECORDED) };
}

function decodeSnaps(s: ControlSeries): HexArea[] {
  return s.snapshots.map((snap) => ({
    hexes: Int32Array.from(snap.hexes, (p) => p[0]),
    frac: Uint8Array.from(snap.hexes, (p) => p[1]),
  }));
}

function geometryLayer(e: ControlEpisode, status: number, inp: ControlInputs): LayerGeo | null {
  const g = e.geometry;
  if ('admin1' in g) {
    return admin1Layer(g.admin1, g.partial ?? [], status, inp.admin1);
  }
  if ('points' in g) {
    return areaLayer(pointsArea(g.points, inp.areaKm2), status);
  }
  if ('file' in g) {
    const geo = inp.geometry.get(g.file);
    if (!geo) {
      inp.warnings.push(`world: control ${e.id}: geometry ${g.file} not loaded; episode has no hexes`);
      return null;
    }
    return areaLayer(polygonArea(geo, inp.areaKm2), status);
  }
  return null;
}

export function buildControl(episodes: ControlEpisode[], inp: ControlInputs): ControlModel {
  const layers: Layer[] = [];
  const series: SeriesLayer[] = [];
  const sorted = episodes.slice().sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));

  for (const e of sorted) {
    const controller = indexOf(inp.actors, e.controller);
    const territory = indexOf(inp.actors, e.territory_of);
    const status = STATUS_CODE[e.status];
    if (controller === 0) {
      inp.warnings.push(`world: control ${e.id}: unknown controller ${e.controller}`);
      continue;
    }

    if ('series' in e.geometry) {
      const s = inp.series.get(e.geometry.series);
      if (!s) {
        inp.warnings.push(`world: control ${e.id}: series ${e.geometry.series} not loaded`);
        continue;
      }
      series.push({ episode: e, controller, territory, status, series: s, snaps: decodeSnaps(s) });
      continue;
    }

    const geo = geometryLayer(e, status, inp);
    if (geo) {
      const extents = ((e as ExtentEpisode).extent ?? []).slice().sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
      layers.push({ episode: e, controller, territory, ...geo, extents, reported: [] });
    }
  }
  return { layers, series, source: inp.source, cache: new Lru(MAX_PICTURES), arrayIds: new WeakMap(), lastId: 0 };
}

// ─── at a date ───────────────────────────────────────────────────────────────────────────

/** Index of the latest snapshot dated ≤ `date`, or -1. */
export function snapAt(s: ControlSeries, date: DateStr): number {
  let best = -1;
  s.snapshots.forEach((snap, i) => {
    if (snap.date <= date) {
      best = i;
    }
  });
  return best;
}

/** The gap of a series that holds `date`, if it declares one. */
export function gapAt(s: ControlSeries, date: DateStr): SeriesGap | undefined {
  const gap = (s as GappedSeries).gaps?.find((g) => g.from <= date && date < g.to);
  return gap && { ...gap, series: s.id };
}

/** Index of the snapshot that describes `date`: the latest one ≤ date, or -1 before the first, outside the episode or inside a gap. */
function observedAt(s: SeriesLayer, date: DateStr): number {
  return inSpan(s.episode, date) && !gapAt(s.series, date) ? snapAt(s.series, date) : -1;
}

interface Part {
  from: DateStr;
  controller: number;
  territory: number;
  hexes: Int32Array;
  frac: Uint8Array;
  code: Uint8Array | number;
  basis: Uint8Array | number;
}

const NO_HEXES = new Int32Array(0);
const NO_FRAC = new Uint8Array(0);

/** A gap's part: its hexes are the whole territory, resolved against the default owner in paint(). */
function unknownPart(s: SeriesLayer): Part {
  return {
    from: s.episode.from, controller: 0, territory: s.territory, hexes: NO_HEXES, frac: NO_FRAC,
    code: CONTROL.CONTESTED, basis: BASIS.UNKNOWN,
  };
}

/** Every hex the default worldview gives `territory`, at GAP_FRAC. */
function territoryPart(p: Part, defOwner: Uint16Array): Part {
  const hexes: number[] = [];
  for (let h = 0; h < defOwner.length; h++) {
    if (defOwner[h] === p.territory) {
      hexes.push(h);
    }
  }
  return { ...p, hexes: Int32Array.from(hexes), frac: new Uint8Array(hexes.length).fill(GAP_FRAC) };
}

/** Index of the latest extent report dated ≤ `date`, or -1 before the first (or with none). */
function extentAt(extents: Extent[], date: DateStr): number {
  return extents.reduce((best, x, k) => (x.date <= date ? k : best), -1);
}

/** The layer's frac and basis with the reported share of extents[k] on its partly held units, made once. */
function reportedShape(l: Layer, k: number): { frac: Uint8Array; basis: Uint8Array } {
  const made = l.reported[k];
  if (made) {
    return made;
  }

  const held = Math.round(l.extents[k].share * FULL);
  const partly = (j: number) => l.basis[j] === BASIS.CONVENTION;
  const shaped = {
    frac: l.frac.map((f, j) => (partly(j) ? held : f)),
    basis: l.basis.map((b, j) => (partly(j) ? BASIS.EXTENT : b)),
  };
  l.reported[k] = shaped;
  return shaped;
}

function activeParts(m: ControlModel, date: DateStr): { parts: Part[]; key: string } {
  const parts: Part[] = [];
  const keys: string[] = [];
  m.layers.forEach((l, i) => {
    if (!inSpan(l.episode, date)) {
      return;
    }

    // The share of a partly held unit is the latest report ≤ D; before the first, the stand-in half.
    const k = extentAt(l.extents, date);
    parts.push({ from: l.episode.from, ...l, ...(k < 0 ? {} : reportedShape(l, k)) });
    keys.push(k < 0 ? String(i) : `${i}e${k}`);
  });
  m.series.forEach((s, i) => {
    if (!inSpan(s.episode, date)) {
      return;
    }
    if (gapAt(s.series, date)) {
      parts.push(unknownPart(s));
      keys.push(`s${i}:gap`);
      return;
    }
    const k = snapAt(s.series, date);
    if (k < 0) {
      return;
    }
    const { hexes, frac } = s.snaps[k];
    parts.push({ from: s.episode.from, controller: s.controller, territory: s.territory, hexes, frac, code: s.status, basis: BASIS.RECORDED });
    keys.push(`s${i}:${k}`);
  });
  parts.sort((a, b) => (a.from < b.from ? -1 : a.from > b.from ? 1 : 0));
  return { parts, key: keys.join(',') };
}

function arrayId(m: ControlModel, arr: Uint16Array): number {
  const hit = m.arrayIds.get(arr);
  if (hit !== undefined) {
    return hit;
  }
  const id = ++m.lastId;
  m.arrayIds.set(arr, id);
  return id;
}

/** A tie by the same controller must not turn its annexation claim back into a lesser status. */
function keepsClaim(prev: { controller: number; code: number }, controller: number, code: number): boolean {
  return prev.controller === controller && prev.code === CONTROL.ANNEXATION_CLAIMED && code !== CONTROL.ANNEXATION_CLAIMED;
}

function paint(parts: Part[], owner: Uint16Array, defOwner: Uint16Array): ControlArrays {
  const controller = new Uint16Array(HEX_COUNT);
  const control = new Uint8Array(HEX_COUNT);
  const controlFrac = new Uint8Array(HEX_COUNT);
  const controlBasis = new Uint8Array(HEX_COUNT);
  const touched: number[] = [];

  for (const part of parts) {
    // A gap part names no hexes: it spans everything its territory owns.
    const p = part.basis === BASIS.UNKNOWN ? territoryPart(part, defOwner) : part;
    for (let j = 0; j < p.hexes.length; j++) {
      const h = p.hexes[j];
      const f = p.frac[j];
      if (f === 0 || f < controlFrac[h] || (p.territory !== 0 && defOwner[h] !== p.territory)) {
        continue;
      }
      const code = typeof p.code === 'number' ? p.code : p.code[j];
      const basis = typeof p.basis === 'number' ? p.basis : p.basis[j];
      if (f === controlFrac[h] && keepsClaim({ controller: controller[h], code: control[h] }, p.controller, code)) {
        continue;
      }
      if (controlFrac[h] === 0) {
        touched.push(h);
      }
      controller[h] = p.controller;
      control[h] = code;
      controlFrac[h] = f;
      controlBasis[h] = basis;
    }
  }

  // Under the leader's view the controller may be the owner: then there is nothing to show.
  for (const h of touched) {
    if (controller[h] === owner[h]) {
      controller[h] = 0;
      control[h] = CONTROL.NONE;
      controlFrac[h] = 0;
      controlBasis[h] = BASIS.RECORDED;
    }
  }
  return { controller, control, controlFrac, controlBasis };
}

/** Control episodes in force at `date` (series episodes only once a snapshot exists). */
export function activeEpisodes(m: ControlModel, date: DateStr): ControlEpisode[] {
  const layers = m.layers.filter((l) => inSpan(l.episode, date)).map((l) => l.episode);
  const series = m.series.filter((s) => inSpan(s.episode, date) && snapAt(s.series, date) >= 0).map((s) => s.episode);
  return [...layers, ...series];
}

/** Control picture at `date` against the leader's owner array and the default owner array. */
export function controlAt(m: ControlModel, date: DateStr, owner: Uint16Array, defOwner: Uint16Array): ControlArrays {
  const { parts, key } = activeParts(m, date);
  const full = `${key}|${arrayId(m, owner)}|${arrayId(m, defOwner)}`;
  return m.cache.use(full, () => paint(parts, owner, defOwner));
}

/**
 * The sources of `ids` that existed on `date`: one published later cannot back what the map shows
 * then (ISW of 26 Sep 2026 under the front of April 2022). A partial date ('2022-02') counts as
 * existing during its whole period. Wikipedia keeps its revision date, so it stays.
 */
export function citedOn(m: ControlModel, ids: readonly SourceId[], date: DateStr): SourceId[] {
  return ids.filter((id) => {
    const s = m.source?.(id);
    return !s?.published || s.publisher === REVISED_PUBLISHER || s.published <= date.slice(0, s.published.length);
  });
}

/** `citedOn`, but every id when all were published later: a fact keeps some source. */
export function citedOrAll(m: ControlModel, ids: readonly SourceId[], date: DateStr): SourceId[] {
  const kept = citedOn(m, ids, date);
  return kept.length > 0 ? kept : [...ids];
}

/** A snapshot's sources on `date`: the ones published by then, else the snapshot's own source. */
function snapCites(m: ControlModel, snap: ControlSeries['snapshots'][number], date: DateStr): SourceId[] {
  const kept = citedOn(m, snap.sources ?? [snap.source], date);
  return kept.length > 0 ? kept : [snap.source];
}

/** Sources of the extent report in force at `date`, when the layer has a partly held unit. */
function reportSources(l: Layer, date: DateStr): SourceId[] {
  const k = extentAt(l.extents, date);
  return k < 0 ? [] : l.extents[k].sources;
}

/** The extent report in force behind the share of `hex` held by `controller` at `date`, or null. */
export function extentOn(m: ControlModel, date: DateStr, hex: number, controller: number): Extent | null {
  for (const l of m.layers) {
    const k = l.controller === controller && inSpan(l.episode, date) ? extentAt(l.extents, date) : -1;
    if (k >= 0 && l.hexes.includes(hex)) {
      return l.extents[k];
    }
  }
  return null;
}

/** Sources of every layer at `date` that holds `hex` for `controller` (the actor index shown there); a series cites only the snapshot in force. */
export function controlSourcesAt(m: ControlModel, date: DateStr, hex: number, controller: number): SourceId[] {
  const out: SourceId[] = [];

  for (const l of m.layers) {
    if (l.controller === controller && inSpan(l.episode, date) && l.hexes.includes(hex)) {
      out.push(...citedOrAll(m, l.episode.sources, date), ...reportSources(l, date));
    }
  }

  for (const s of m.series) {
    const k = observedAt(s, date);
    if (s.controller !== controller || k < 0 || !s.snaps[k].hexes.includes(hex)) {
      continue;
    }
    const snap = s.series.snapshots[k];
    out.push(...snapCites(m, snap, date), ...citedOn(m, s.episode.sources, date));
  }
  return [...new Set(out)];
}

/** Front lines in force at `date`: each series' latest snapshot ≤ date inside its episode; none inside a gap. */
export function frontsAt(m: ControlModel, date: DateStr): NonNullable<WorldSnapshot['fronts']> {
  const out: NonNullable<WorldSnapshot['fronts']> = [];
  for (const s of m.series) {
    const k = observedAt(s, date);
    const snap = k < 0 ? null : s.series.snapshots[k];
    if (!snap?.line?.length) {
      continue;
    }
    out.push({
      id: s.series.id,
      controller: s.series.controller,
      lines: snap.line,
      asOf: snap.date,
      sources: [...new Set([...snapCites(m, snap, date), ...citedOn(m, s.episode.sources, date)])],
    });
  }
  return out;
}

/** The declared gaps holding `date` in the series of episodes in force: the pictures the map does not have. */
export function gapsAt(m: ControlModel, date: DateStr): SeriesGap[] {
  return m.series.flatMap((s) => {
    const gap = inSpan(s.episode, date) ? gapAt(s.series, date) : undefined;
    return gap ? [gap] : [];
  });
}

/** Land a war's occupier holds, per dated snapshot (src/ui/extensions.ts ControlHistory). */
export interface ControlHistory {
  controller: string;
  territoryOf: string;
  points: Array<{ date: DateStr; share: number; area_km2: number }>;
  sources: SourceId[];
}

/**
 * The held area at each snapshot of a war's control series up to `date`, as a share of the whole
 * territory it is taken from (Ukraine: 117,089 km² of 613,860 km² = 0.19). Null when the war has no
 * series, no snapshot exists yet, or the territory is not a country with an area.
 */
export function controlHistory(
  m: ControlModel, warId: string, date: DateStr, territory: (iso3: string) => Pick<Country, 'area_km2' | 'sources'> | undefined,
): ControlHistory | null {
  const s = m.series.find((x) => x.episode.war === warId);
  const whole = s && territory(s.episode.territory_of);
  if (!s || !whole || whole.area_km2 <= 0) {
    return null;
  }

  const snaps = s.series.snapshots.filter((x) => x.date <= date);
  if (snaps.length === 0) {
    return null;
  }
  return {
    controller: s.episode.controller,
    territoryOf: s.episode.territory_of,
    points: snaps.map((x) => ({ date: x.date, share: Math.min(1, x.area_km2 / whole.area_km2), area_km2: x.area_km2 })),
    sources: [...new Set([...snaps.flatMap((x) => x.sources ?? [x.source]), ...s.episode.sources, ...whole.sources])],
  };
}
